// ./src/utils/formatHelpers.ts

// ============================================
// Conversion Constants
// ============================================

const KM_TO_MILES = 0.621371;
const MILES_TO_KM = 1.60934;
const US_GALLONS_TO_LITERS = 3.78541;
const UK_GALLONS_TO_LITERS = 4.54609;
const LITERS_TO_US_GALLONS = 1 / US_GALLONS_TO_LITERS; // ≈ 0.264172
const LITERS_TO_UK_GALLONS = 1 / UK_GALLONS_TO_LITERS; // ≈ 0.219969

// ============================================
// Number Formatting
// ============================================

/**
 * Format a number with locale-aware thousands separators
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 0)
 * @param locale - Locale string (default: user's locale)
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number | null | undefined,
  decimals: number = 0,
  locale?: string
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "-";
  }

  return value.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format a number as a compact representation (e.g., 1.2K, 3.5M)
 * @param value - The number to format
 * @param locale - Locale string (default: user's locale)
 * @returns Compact formatted number string
 */
export const formatCompactNumber = (
  value: number | null | undefined,
  locale?: string
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "-";
  }

  return value.toLocaleString(locale, {
    notation: "compact",
    compactDisplay: "short",
  });
};

// ============================================
// Currency Formatting
// ============================================

/**
 * The currency part of a formatted amount — never a bare "$" (owner, 2026-10-03: "we need to always show what type of
 * currency it is. For USD we need US$").
 *
 * ⚠ A locale writes ITS OWN dollar as "$" — en-US for USD, en-CA for CAD, en-AU for AUD — so "$" alone never says
 *   which, and an event or a receipt can be read by someone in another country. A bare "$" becomes the en-US standard
 *   symbol ("CA$", "A$", "NZ$"), and for USD, whose en-US symbol is itself "$", "US$". Every other symbol is kept.
 */
export const currencySymbolLabel = (symbol: string, currency: string): string => {
  if (symbol.trim() !== "$") {
    return symbol;
  }

  const code = String(currency || "").trim().toUpperCase();

  if (code === "USD" || !code) {
    return "US$";
  }

  try {
    const standard = new Intl.NumberFormat("en-US", { style: "currency", currency: code })
      .formatToParts(0)
      .find((p) => p.type === "currency")?.value;

    if (standard && standard.trim() !== "$") {
      return standard;
    }
  } catch {
    // An unknown code: fall through to the code itself.
  }

  return code;
};

/**
 * A currency formatter's output with its currency part run through `currencySymbolLabel`. ⓘ A replacement that ends (or
 * starts) with a letter is kept off the digits with a no-break space — a locale that writes its own "$" flush against
 * the number would otherwise print "SGD1,234.50".
 */
export const formatWithCurrencyLabel = (formatter: Intl.NumberFormat, amount: number, currency: string): string => {
  const parts = formatter.formatToParts(amount);

  return parts
    .map((p, i) => {
      if (p.type !== "currency") {
        return p.value;
      }

      const label = currencySymbolLabel(p.value, currency);

      if (label === p.value) {
        return label;
      }

      const next = parts[i + 1]?.value ?? "";
      const prev = parts[i - 1]?.value ?? "";
      const after = /[A-Za-z]$/.test(label) && /^[\d\-−+(]/.test(next) ? "\u00a0" : "";
      const before = /^[A-Za-z]/.test(label) && /[\d)]$/.test(prev) ? "\u00a0" : "";

      return `${before}${label}${after}`;
    })
    .join("");
};

/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param currency - Currency code (e.g., "USD", "EUR", "CAD")
 * @param locale - Locale string (default: user's locale)
 * @param options - Additional Intl.NumberFormat options
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number | null | undefined,
  currency: string = "USD",
  locale?: string,
  options?: Partial<Intl.NumberFormatOptions>
): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "-";
  }

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...options,
    });

    return formatWithCurrencyLabel(formatter, amount, currency);
  } catch (error) {
    // Fallback if currency code is invalid
    return `${currency} ${formatNumber(amount, 2)}`;
  }
};

/**
 * Format currency with compact notation for large amounts
 * @param amount - The amount to format
 * @param currency - Currency code
 * @param locale - Locale string
 * @returns Compact formatted currency string
 */
export const formatCurrencyCompact = (
  amount: number | null | undefined,
  currency: string = "USD",
  locale?: string
): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "-";
  }

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      notation: "compact",
      compactDisplay: "short",
    });

    return formatWithCurrencyLabel(formatter, amount, currency);
  } catch (error) {
    return `${currency} ${formatCompactNumber(amount)}`;
  }
};

// ============================================
// Distance Formatting
// ============================================

/**
 * Convert kilometers to miles
 */
export const kmToMiles = (km: number): number => km * KM_TO_MILES;

/**
 * Convert miles to kilometers
 */
export const milesToKm = (miles: number): number => miles * MILES_TO_KM;

/**
 * Format distance with unit
 * @param valueInKm - Distance in kilometers (always stored in km)
 * @param unit - Target unit ("km" or "mi")
 * @param decimals - Number of decimal places
 * @param includeUnit - Whether to include unit label
 * @returns Formatted distance string
 */
export const formatDistance = (
  valueInKm: number | null | undefined,
  unit: string = "km",
  decimals: number = 0,
  includeUnit: boolean = true
): string => {
  if (valueInKm === null || valueInKm === undefined || isNaN(valueInKm)) {
    return "-";
  }

  const convertedValue = unit === "mi" ? kmToMiles(valueInKm) : valueInKm;
  const formatted = formatNumber(convertedValue, decimals);

  if (!includeUnit) {
    return formatted;
  }

  return `${formatted} ${unit}`;
};

/**
 * Format odometer reading (typically whole numbers, no decimals)
 * @param valueInKm - Odometer reading in kilometers
 * @param unit - Target unit
 * @returns Formatted odometer string
 */
export const formatOdometer = (
  valueInKm: number | null | undefined,
  unit: string = "km"
): string => {
  return formatDistance(valueInKm, unit, 0, true);
};



// ============================================
// Volume Formatting
// ============================================

/**
 * Convert liters to the specified volume unit
 * @param liters - Volume in liters
 * @param unit - Target volume unit ("l", "gal-us", "gal-uk")
 * @returns Converted volume
 */
export const litersToVolumeUnit = (
  liters: number,
  unit: string
): number => {
  switch (unit) {
    case "gal-us":
      return liters * LITERS_TO_US_GALLONS;
    case "gal-uk":
      return liters * LITERS_TO_UK_GALLONS;
    case "l":
    default:
      return liters;
  }
};

/**
 * Convert from volume unit to liters
 * @param value - Volume in the specified unit
 * @param unit - Source volume unit ("l", "gal-us", "gal-uk")
 * @returns Volume in liters
 */
export const volumeUnitToLiters = (value: number, unit: string): number => {
  switch (unit) {
    case "gal-us":
      return value * US_GALLONS_TO_LITERS;
    case "gal-uk":
      return value * UK_GALLONS_TO_LITERS;
    case "l":
    default:
      return value;
  }
};

/**
 * @deprecated Use litersToVolumeUnit instead
 * Convert liters to US gallons
 */
export const litersToGallons = (liters: number): number =>
  liters * LITERS_TO_US_GALLONS;

/**
 * @deprecated Use volumeUnitToLiters instead
 * Convert US gallons to liters
 */
export const gallonsToLiters = (gallons: number): number =>
  gallons * US_GALLONS_TO_LITERS;

/**
 * Format volume with unit
 * @param valueInLiters - Volume in liters (always stored in liters)
 * @param unit - Target unit ("l", "gal-us", or "gal-uk")
 * @param decimals - Number of decimal places
 * @param includeUnit - Whether to include unit label
 * @returns Formatted volume string
 */
export const formatVolume = (
  valueInLiters: number | null | undefined,
  unit: string = "l",
  decimals: number = 2,
  includeUnit: boolean = true
): string => {
  if (
    valueInLiters === null ||
    valueInLiters === undefined ||
    isNaN(valueInLiters)
  ) {
    return "-";
  }

  const convertedValue = litersToVolumeUnit(valueInLiters, unit);
  const formatted = formatNumber(convertedValue, decimals);

  if (!includeUnit) {
    return formatted;
  }

  const label = getVolumeLabel(unit, "short");
  return `${formatted} ${label}`;
};

/**
 * Get volume unit label
 * @param unit - Volume unit
 * @param variant - "short" (L/gal) or "long" (liters/gallons)
 * @returns Unit label string
 */
export const getVolumeLabel = (
  unit: string = "l",
  variant: "short" | "long" = "short"
): string => {
  if (variant === "long") {
    switch (unit) {
      case "gal-us":
        return "gallons (US)";
      case "gal-uk":
        return "gallons (UK)";
      case "kwh":
        return "kilowatt-hours";
      case "l":
      default:
        return "liters";
    }
  }

  // Short variant
  switch (unit) {
    case "gal-us":
    case "gal-uk":
      return "gal";
    case "kwh":
      return "kWh";
    case "l":
    default:
      return "L";
  }
};

// ============================================
// Energy Formatting (for electric vehicles)
// ============================================

/**
 * Format energy with unit (for electric vehicles)
 * @param valueInKwh - Energy in kWh
 * @param decimals - Number of decimal places
 * @param includeUnit - Whether to include unit label
 * @returns Formatted energy string
 */
export const formatEnergy = (
  valueInKwh: number | null | undefined,
  decimals: number = 1,
  includeUnit: boolean = true
): string => {
  if (
    valueInKwh === null ||
    valueInKwh === undefined ||
    isNaN(valueInKwh)
  ) {
    return "-";
  }

  const formatted = formatNumber(valueInKwh, decimals);

  if (!includeUnit) {
    return formatted;
  }

  return `${formatted} kWh`;
};

// ============================================
// Fuel Efficiency Formatting
// ============================================

/**
 * Convert L/100km to specified consumption unit
 * @param lper100km - Consumption in L/100km
 * @param unit - Target consumption unit
 * @returns Converted consumption value
 */
export const lper100kmToConsumptionUnit = (
  lper100km: number,
  unit: string
): number => {
  if (lper100km <= 0) return 0;

  switch (unit) {
    case "l100km":
      return lper100km;

    case "km-l":
      // km/L = 100 / (L/100km)
      return 100 / lper100km;

    case "mpg-us":
      // MPG (US) = 235.215 / (L/100km)
      return 235.215 / lper100km;

    case "mpg-uk":
      // MPG (UK) = 282.481 / (L/100km)
      return 282.481 / lper100km;

    case "mi-l":
      // mi/L = km/L * km_to_miles = (100 / L/100km) * 0.621371
      return (100 / lper100km) * KM_TO_MILES;

    default:
      return lper100km;
  }
};

/**
 * Convert kWh/100km to specified consumption unit
 * Note: Only kwh100km is directly supported; other units are for liquid fuels
 * @param kwhPer100km - Consumption in kWh/100km
 * @param unit - Target consumption unit
 * @returns Converted consumption value (or original if unit doesn't apply)
 */
export const kwhPer100kmToConsumptionUnit = (
  kwhPer100km: number,
  unit: string
): number => {
  if (kwhPer100km <= 0) return 0;

  switch (unit) {
    case "kwh100km":
      return kwhPer100km;

    case "km-kwh":
      // km/kWh = 100 / (kWh/100km)
      return 100 / kwhPer100km;

    default:
      // For liquid fuel units, return as-is (shouldn't mix units)
      return kwhPer100km;
  }
};

/**
 * Convert from consumption unit to L/100km
 * @param value - Consumption in the specified unit
 * @param unit - Source consumption unit
 * @returns Consumption in L/100km
 */
export const consumptionUnitToLper100km = (
  value: number,
  unit: string
): number => {
  if (value <= 0) return 0;

  switch (unit) {
    case "l100km":
      return value;

    case "km-l":
      // L/100km = 100 / (km/L)
      return 100 / value;

    case "mpg-us":
      // L/100km = 235.215 / MPG (US)
      return 235.215 / value;

    case "mpg-uk":
      // L/100km = 282.481 / MPG (UK)
      return 282.481 / value;

    case "mi-l":
      // L/100km = 100 / (mi/L / km_to_miles)
      return 100 / (value / KM_TO_MILES);

    default:
      return value;
  }
};

/**
 * @deprecated Use lper100kmToConsumptionUnit instead
 * Convert L/100km to MPG (US)
 */
export const lper100kmToMpg = (lper100km: number): number => {
  if (lper100km <= 0) return 0;
  return 235.215 / lper100km;
};

/**
 * @deprecated Use consumptionUnitToLper100km instead
 * Convert MPG (US) to L/100km
 */
export const mpgToLper100km = (mpg: number): number => {
  if (mpg <= 0) return 0;
  return 235.215 / mpg;
};

/**
 * Format fuel efficiency with unit
 * @param valueInLper100km - Efficiency in L/100km (always stored in L/100km)
 * @param unit - Target consumption unit
 * @param decimals - Number of decimal places
 * @param includeUnit - Whether to include unit label
 * @returns Formatted efficiency string
 */
export const formatEfficiency = (
  valueInLper100km: number | null | undefined,
  unit: string = "l100km",
  decimals: number = 1,
  includeUnit: boolean = true
): string => {
  if (
    valueInLper100km === null ||
    valueInLper100km === undefined ||
    isNaN(valueInLper100km) ||
    valueInLper100km <= 0
  ) {
    return "-";
  }

  const convertedValue = lper100kmToConsumptionUnit(valueInLper100km, unit);
  const formatted = formatNumber(convertedValue, decimals);

  if (!includeUnit) {
    return formatted;
  }

  const label = getEfficiencyLabel(unit);
  return `${formatted} ${label}`;
};

/**
 * Format electric efficiency with unit
 * @param valueInKwhPer100km - Efficiency in kWh/100km
 * @param unit - Target consumption unit (kwh100km or km-kwh)
 * @param decimals - Number of decimal places
 * @param includeUnit - Whether to include unit label
 * @returns Formatted efficiency string
 */
export const formatElectricEfficiency = (
  valueInKwhPer100km: number | null | undefined,
  unit: string = "kwh100km",
  decimals: number = 1,
  includeUnit: boolean = true
): string => {
  if (
    valueInKwhPer100km === null ||
    valueInKwhPer100km === undefined ||
    isNaN(valueInKwhPer100km) ||
    valueInKwhPer100km <= 0
  ) {
    return "-";
  }

  const convertedValue = kwhPer100kmToConsumptionUnit(valueInKwhPer100km, unit);
  const formatted = formatNumber(convertedValue, decimals);

  if (!includeUnit) {
    return formatted;
  }

  const label = getEfficiencyLabel(unit);
  return `${formatted} ${label}`;
};

/**
 * Format fuel efficiency that's already in the target unit (no conversion needed)
 * Use this when the API has already converted to user's preferred unit
 * @param value - Efficiency already in target unit
 * @param unit - Consumption unit (for label only)
 * @param decimals - Number of decimal places
 * @param includeUnit - Whether to include unit label
 * @returns Formatted efficiency string
 */
export const formatEfficiencyRaw = (
  value: number | null | undefined,
  unit: string = "l100km",
  decimals: number = 1,
  includeUnit: boolean = true
): string => {
  if (
    value === null ||
    value === undefined ||
    isNaN(value) ||
    value <= 0
  ) {
    return "-";
  }

  const formatted = formatNumber(value, decimals);

  if (!includeUnit) {
    return formatted;
  }

  const label = getEfficiencyLabel(unit);
  return `${formatted} ${label}`;
};

/**
 * Get efficiency unit label
 * @param unit - Consumption unit
 * @returns Unit label string
 */
export const getEfficiencyLabel = (
  unit: string = "l100km"
): string => {
  switch (unit) {
    case "l100km":
      return "L/100km";
    case "kwh100km":
      return "kWh/100km";
    case "km-l":
      return "km/L";
    case "km-kwh":
      return "km/kWh";
    case "mpg-us":
    case "mpg-uk":
      return "MPG";
    case "mi-l":
      return "mi/L";
    default:
      return unit;
  }
};

// ============================================
// Percentage Formatting
// ============================================

/**
 * Format a number as percentage
 * @param value - The value (0-100 or 0-1 depending on isDecimal)
 * @param decimals - Number of decimal places
 * @param isDecimal - If true, value is 0-1 (will multiply by 100)
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number | null | undefined,
  decimals: number = 0,
  isDecimal: boolean = false
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "-";
  }

  const percentValue = isDecimal ? value * 100 : value;
  return `${formatNumber(percentValue, decimals)}%`;
};

// ============================================
// Price Per Unit Formatting
// ============================================

/**
 * Format price per liter/gallon
 * @param pricePerLiter - Price per liter
 * @param currency - Currency code
 * @param volumeUnit - Volume unit for display
 * @returns Formatted price per unit string
 */
export const formatPricePerVolume = (
  pricePerLiter: number | null | undefined,
  currency: string = "USD",
  volumeUnit: string = "l",
  displayUnit: boolean = true
): string => {
  if (
    pricePerLiter === null ||
    pricePerLiter === undefined ||
    isNaN(pricePerLiter)
  ) {
    return "-";
  }

  // Convert price: price per gallon = price per liter * liters per gallon
  let convertedPrice: number;
  switch (volumeUnit) {
    case "gal-us":
      convertedPrice = pricePerLiter * US_GALLONS_TO_LITERS;
      break;
    case "gal-uk":
      convertedPrice = pricePerLiter * UK_GALLONS_TO_LITERS;
      break;
    case "l":
    default:
      convertedPrice = pricePerLiter;
  }

  const formatted = formatCurrency(convertedPrice, currency);

  if (!displayUnit) {
    return formatted;
  }

  const unitLabel = getVolumeLabel(volumeUnit, "short");

  return `${formatted}/${unitLabel}`;
};

/**
 * Format price per distance unit (cost per km or mile)
 * @param pricePerKm - Price per kilometer
 * @param currency - Currency code
 * @param distanceUnit - Distance unit for display
 * @returns Formatted price per unit string
 */
export const formatPricePerDistance = (
  pricePerKm: number | null | undefined,
  currency: string = "USD",
  distanceUnit: string = "km"
): string => {
  if (pricePerKm === null || pricePerKm === undefined || isNaN(pricePerKm)) {
    return "-";
  }

  // Convert price: price per mile = price per km * km per mile
  const convertedPrice =
    distanceUnit === "mi" ? pricePerKm * MILES_TO_KM : pricePerKm;

  const formatted = formatCurrency(convertedPrice, currency);
  const unitLabel = distanceUnit === "mi" ? "mi" : "km";

  return `${formatted}/${unitLabel}`;
};

// ============================================
// Change/Trend Formatting
// ============================================

/**
 * Format a change value with + or - prefix
 * @param value - The change value
 * @param decimals - Number of decimal places
 * @returns Formatted change string with sign
 */
export const formatChange = (
  value: number | null | undefined,
  decimals: number = 0
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "-";
  }

  const formatted = formatNumber(Math.abs(value), decimals);
  if (value > 0) {
    return `+${formatted}`;
  } else if (value < 0) {
    return `-${formatted}`;
  }
  return formatted;
};

/**
 * Format a percentage change with + or - prefix
 * @param value - The percentage change value
 * @param decimals - Number of decimal places
 * @returns Formatted percentage change string with sign
 */
export const formatPercentageChange = (
  value: number | null | undefined,
  decimals: number = 1
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return "-";
  }

  const formatted = formatNumber(Math.abs(value), decimals);
  if (value > 0) {
    return `+${formatted}%`;
  } else if (value < 0) {
    return `-${formatted}%`;
  }
  return `${formatted}%`;
};